import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Divider,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  Link,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton,
} from '@mui/material';
import {
  Send as SendIcon,
  Cancel as CancelIcon,
  NotificationsNone as RemindIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as PendingIcon,
  SkipNext as SkippedIcon,
  Email as EmailIcon,
  AssignmentTurnedIn as DoneIcon,
} from '@mui/icons-material';
import StatusPill from './StatusPill';
import {
  useClientRequest,
  useCancelRequest,
  useRemindRequest,
  useSendRequest,
} from '../../hooks/useClientRequests';
import { format, parseISO } from 'date-fns';

const ITEM_TYPE_LABELS = {
  document_upload: 'Document Upload',
  text_answer: 'Text Answer',
  multiple_choice: 'Multiple Choice',
  checkbox: 'Checkbox',
  esignature: 'E-Signature',
  file_review: 'File Review',
};

function ItemStatusIcon({ status }) {
  if (status === 'fulfilled') return <CheckIcon color="success" fontSize="small" />;
  if (status === 'skipped') return <SkippedIcon color="disabled" fontSize="small" />;
  return <PendingIcon color="action" fontSize="small" />;
}

function ResponseBlock({ item }) {
  const t = item.item_type;
  if (item.status !== 'fulfilled') return null;

  if (t === 'document_upload' && item.uploaded_document_id) {
    return (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        Uploaded file #{item.uploaded_document_id}{' '}
        <Link href={`/files/clients/root`} underline="hover">
          Open in Files
        </Link>
      </Typography>
    );
  }
  if (t === 'text_answer' && item.response_text) {
    return (
      <Paper variant="outlined" sx={{ mt: 1, p: 1.5, bgcolor: 'background.default' }}>
        <Typography variant="body2" component="blockquote" sx={{ m: 0, pl: 1, borderLeft: 3, borderColor: 'divider' }}>
          {item.response_text}
        </Typography>
      </Paper>
    );
  }
  if (t === 'multiple_choice' && item.response_choice) {
    const v = Array.isArray(item.response_choice) ? item.response_choice[0] : item.response_choice;
    return (
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Selected: <strong>{String(v)}</strong>
      </Typography>
    );
  }
  if (t === 'checkbox' && item.response_choice) {
    const arr = Array.isArray(item.response_choice) ? item.response_choice : [item.response_choice];
    return (
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Selected: {arr.join(', ')}
      </Typography>
    );
  }
  if (t === 'esignature') {
    return (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        Document signed.
      </Typography>
    );
  }
  if (t === 'file_review') {
    return (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        Client acknowledged review.
      </Typography>
    );
  }
  return null;
}

function DetailSkeleton() {
  return (
    <Box>
      <Skeleton variant="text" width={280} height={36} />
      <Skeleton variant="text" width={200} height={24} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={8} sx={{ mb: 3 }} />
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} variant="rounded" height={72} sx={{ mb: 1 }} />
      ))}
    </Box>
  );
}

export default function ClientRequestDetail({ requestId, onBack }) {
  const { data: req, isLoading, isError, error, refetch } = useClientRequest(requestId);
  const [snack, setSnack] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const cancelMutation = useCancelRequest();
  const remindMutation = useRemindRequest();
  const sendMutation = useSendRequest();

  const handleSend = async () => {
    setActionLoading(true);
    try {
      await sendMutation.mutateAsync(requestId);
      setSnack({ severity: 'success', message: 'Request sent to client.' });
      refetch();
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.error || 'Failed to send.' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemind = async () => {
    setActionLoading(true);
    try {
      await remindMutation.mutateAsync(requestId);
      setSnack({ severity: 'success', message: 'Reminder sent.' });
      refetch();
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.error || 'Failed to send reminder.' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelOpen(false);
    setActionLoading(true);
    try {
      await cancelMutation.mutateAsync(requestId);
      setSnack({ severity: 'info', message: 'Request cancelled.' });
      refetch();
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.error || 'Failed to cancel.' });
    } finally {
      setActionLoading(false);
    }
  };

  if (isLoading) {
    return <DetailSkeleton />;
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        {error?.response?.data?.error || error?.message || 'Failed to load request.'}
      </Alert>
    );
  }

  if (!req) return null;

  const items = [...(req.items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const total = req.items_total || items.length;
  const fulfilled = req.items_fulfilled || items.filter((i) => i.status === 'fulfilled').length;
  const pct = total > 0 ? Math.round((fulfilled / total) * 100) : 0;
  const canSend = req.status === 'draft';
  const canRemind = ['sent', 'in_progress', 'partially_complete', 'overdue'].includes(req.status);
  const canCancel = req.status !== 'complete' && req.status !== 'cancelled';

  const timelineEvents = [];
  if (req.sent_at) {
    timelineEvents.push({ kind: 'sent', at: req.sent_at, label: 'Request sent to client' });
  }
  if (req.last_reminder_sent_at) {
    timelineEvents.push({
      kind: 'remind',
      at: req.last_reminder_sent_at,
      label: 'Reminder sent',
    });
  }
  items.forEach((it) => {
    if (it.fulfilled_at) {
      timelineEvents.push({
        kind: 'item',
        at: it.fulfilled_at,
        label: `Fulfilled: ${it.label}`,
      });
    }
  });
  if (req.completed_at) {
    timelineEvents.push({ kind: 'done', at: req.completed_at, label: 'Request completed' });
  }
  timelineEvents.sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <Box>
      {onBack && (
        <Button size="small" onClick={onBack} sx={{ mb: 2 }}>
          ← Back to list
        </Button>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700}>
            {req.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {req.client_name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatusPill status={req.status} />
          {req.due_date && (
            <Chip label={`Due ${new Date(req.due_date).toLocaleDateString()}`} size="small" variant="outlined" />
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {canSend && (
          <Button
            variant="contained"
            size="small"
            startIcon={<SendIcon fontSize="small" />}
            onClick={handleSend}
            disabled={actionLoading}
          >
            Send to Client
          </Button>
        )}
        {canRemind && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<RemindIcon fontSize="small" />}
            onClick={handleRemind}
            disabled={actionLoading}
          >
            Send reminder
          </Button>
        )}
        {canCancel && (
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<CancelIcon fontSize="small" />}
            onClick={() => setCancelOpen(true)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
        )}
      </Box>

      {req.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {req.description}
        </Typography>
      )}

      {total > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>
              Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {fulfilled}/{total} items
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Items
      </Typography>
      <List dense disablePadding sx={{ mb: 3 }}>
        {items.map((item) => (
          <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
            <Paper variant="outlined" sx={{ width: '100%', px: 2, py: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <ItemStatusIcon status={item.status} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {item.label}
                  </Typography>
                  {item.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    <Chip
                      label={ITEM_TYPE_LABELS[item.item_type] || item.item_type}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                    <Chip
                      label={
                        item.status === 'fulfilled'
                          ? 'Fulfilled'
                          : item.status === 'skipped'
                            ? 'Skipped'
                            : 'Pending'
                      }
                      size="small"
                      sx={{ fontSize: 11 }}
                    />
                    {!item.is_required && <Chip label="Optional" size="small" sx={{ fontSize: 11 }} />}
                  </Box>
                  <ResponseBlock item={item} />
                </Box>
              </Box>
            </Paper>
          </ListItem>
        ))}
        {items.length === 0 && (
          <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>
            No items on this request.
          </Typography>
        )}
      </List>

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Activity
      </Typography>
      {timelineEvents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No activity recorded yet.
        </Typography>
      ) : (
        <List dense>
          {timelineEvents.map((ev, idx) => (
            <ListItem key={`${ev.kind}-${ev.at}-${idx}`} sx={{ py: 0.75, alignItems: 'flex-start' }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                {ev.kind === 'remind' ? (
                  <EmailIcon fontSize="small" color="action" />
                ) : ev.kind === 'done' ? (
                  <DoneIcon fontSize="small" color="success" />
                ) : (
                  <SendIcon fontSize="small" color="action" />
                )}
              </ListItemIcon>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {(() => {
                    try {
                      return format(parseISO(ev.at), 'PPp');
                    } catch {
                      return ev.at;
                    }
                  })()}
                </Typography>
                <Typography variant="body2">{ev.label}</Typography>
              </Box>
            </ListItem>
          ))}
        </List>
      )}

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)}>
        <DialogTitle>Cancel Request?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cancel &quot;{req.title}&quot;? The client will no longer be able to submit items.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Keep</Button>
          <Button color="error" variant="contained" onClick={handleCancel}>
            Cancel Request
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack && (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}
